import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  handlePODetailsClick,
  handleCRHistoryClick,
  Filter,
  downloadCSV,
  MultiSelectColumnFilter,
  CalculateTableArray
} from '../utils/Utils'
import { IndeterminateCheckbox } from './buyer/NewPoList'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues
} from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { CommonAPICall } from '../CommonAPiContext/context'
import axiosInstance, { portInUseForAPI } from '../api/axiosInstance'
import {
  MessageIconRead,
  MessageIconUnread
} from '../components/MessageIcon'
import MessageBox from '../components/MessageBox'
import { CartState } from '../Context/Context'
import PopupBanner from '../components/PopupBanner'

const API = `${process.env.REACT_APP_API_DOMAIN}:${portInUseForAPI}`

const TABLE_NAME = 'MyPos'
const COLUMN_RESIZE_MODE = 'onChange'
const COLUMN_RESIZE_DIRECTION = 'ltr'


const blankIfEmpty = (v) => (!v || String(v).trim() === '' ? 'BLANK' : v)
const includesFilter = (row, id, value) => value.includes(row.getValue(id))
const textCell = (key) => ({ row }) =>
  row?.original?.[key] ? <span>{row.original[key]}</span> : null

const blankColumn = (header, key, extra = {}) => ({
  header,
  accessorFn: (row) => blankIfEmpty(row[key]),
  cell: textCell(key),
  filterFn: includesFilter,
  ...extra
})

const SORT_ICONS = {
  asc: { viewBox: '0 0 10 14', d: 'M5 13V1m0 0L1 5m4-4 4 4' },
  desc: { viewBox: '0 0 10 14', d: 'M5 1v12m0 0 4-4m-4 4L1 9' },
  none: { viewBox: '0 0 16 20', d: 'M4 6v13m0 0 3-3m-3 3-3-3m11-2V1m0 0L9 4m3-3 3 3' }
}

const SortIcon = ({ direction }) => {
  const { viewBox, d } = SORT_ICONS[direction || 'none']
  return (
    <svg
      className='w-3 h-4 text-gray-500'
      aria-hidden='true'
      xmlns='http://www.w3.org/2000/svg'
      fill='none'
      viewBox={viewBox}
    >
      <path stroke='currentColor' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d={d} />
    </svg>
  )
}

const PoField = ({ label, value, className = '' }) => (
  <div className={className}>
    <p className='text-[10px] font-semibold uppercase text-gray-500'>{label}</p>
    <p className='text-sm break-words'>{value}</p>
  </div>
)


const MessageView = () => {
  const navigate = useNavigate()
  const user = JSON.parse(localStorage.getItem('userData'))
  const { addFilterData } = CommonAPICall()
  const isVendor = user?.userGroupName === "SUPPLIER"

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [sorting, setSorting] = useState([])
  const [rowSelection, setRowSelection] = useState({})
  const [columnFilters, setColumnFilters] = useState([])
  const [columnOrder, setColumnOrder] = useState()
  const [columnVisibility, setColumnVisibility] = useState()
  const [showSelectColumn, setShowSelectColumn] = useState(false)
  const [filterList, setFilterList] = useState({})
  const [values, setValues] = useState([])
  const [clearState, setClearState] = useState(false)

  const [isMessageBoxOpen, setIsMessageBoxOpen] = useState(false)
  const [selectedPO, setSelectedPO] = useState(null)
  const [selectedSKU, setSelectedSKU] = useState(null)
  const [marking, setMarking] = useState(false)
  const [selectedRowValues, setSelectedRowValues] = useState([])
  const [counts, setCounts] = useState({ total: 0, unseen: 0, seen: 0 })
  const [statusFilter, setStatusFilter] = useState('ALL')
  const columnBeingDragged = useRef(-1)
  const role = localStorage.getItem('role');
  const displayName = localStorage.getItem("displayName")
  const isUnread = (row) => row?.STATUS === 'N'
  const [messageForBanner, setMessageForBanner] = useState('')
  const forceBg = (color) => (el) => {
    if (el) el.style.setProperty('background-color', color, 'important')
  }
  const {
    getUnseenMessage,
  } = CartState()

  const { t } = useTranslation()
  const { Columns, ToggleAll, ClearFilters, Page, Gotopage, Show, Loading1, MessageView, Home, MarkAsRead, MarkAsUnread,
    READ, UNREAD, ALL  } = t('MessageView')

  const headers = {
    PONUMBER: t('POList.PONUMBER'),
    SKU: t('POList.SKU'),
    UPC: t('POList.UPC'),
    DC: t('POList.DC'),
    REQSW: t('POHeaderDetails.REQSW'),
    ORGSW: t('POList.ORGSW'),
    CURRSW: t('POList.CURRENTSW'),
    ONTIMETARGET: t('POList.ONTIMETARGET'),
    UNITCOST: t('POList.UNITCOST'),
    NOTETOVENDOR: t('POList.NOTETOVENDOR'),
    POQUANTITY: t('POList.POQUANTITY'),
    VENDORMESSAGE: t('POList.VENDORMESSAGE'),
    BUYERMESSAGE: t('POList.BUYERMESSAGE'),
    POSKU: t('POList.POSKU'),
    FACTORYCOUNTRY: t('POList.FACTORYCOUNTRY'),
    CARGOREADYSW: t('POList.CARGOREADYSW')
  }

  const columns = useMemo(
    () => [
      {
        header: headers.POSKU,
        accessorKey: 'PO_SKU',
        filterFn: includesFilter,
        isPinned: 'left'
      },
      {
        header: headers.PONUMBER,
        accessorKey: 'PO_NUMBER',
        cell: (info) => (
          <p
            className='hover:underline cursor-pointer text-blue-500'
            onClick={(e) => {
              e.stopPropagation()
              handlePODetailsClick('vendor', info?.getValue())
            }}
          >
            {info?.getValue()}
          </p>
        ),
        Filter: MultiSelectColumnFilter,
        filter: 'multiSelect',
        filterFn: includesFilter,
        isPinned: 'left'
      },
      { header: headers.SKU, accessorKey: 'SKU', filterFn: includesFilter },
      { header: headers.UPC, accessorKey: 'UPC', filterFn: includesFilter },
      blankColumn(headers.BUYERMESSAGE, 'BUYER_MESSAGE', { size: 100 }),
      blankColumn(headers.VENDORMESSAGE, 'VENDOR_MESSAGE', { size: 100 }),
      blankColumn(headers.DC, 'DC', { size: 100 }),
      {
        header: headers.UNITCOST,
        accessorKey: 'UNIT_COST',
        cell: ({ getValue }) => `$ ${getValue()}`
      },
      {
        header: headers.NOTETOVENDOR,
        accessorFn: (row) => blankIfEmpty(row.note_to_vendor),
        cell: textCell('note_to_vendor'),
        filterFn: (row, id, value) => {
          const cellValue = String(row.getValue(id)).trim()
          if (typeof value === 'string' && value.includes(',')) {
            return value.split(',').map((v) => v.trim()).includes(cellValue)
          }
          return cellValue === String(value).trim()
        }
      },
      {
        header: headers.FACTORYCOUNTRY,
        accessorKey: 'FACTORY_COUNTRY',
        filterFn: includesFilter
      },
      blankColumn(headers.CURRSW, 'CURRENT_SW'),
      { header: headers.POQUANTITY, accessorKey: 'QUANTITY' }
    ],
    Object.values(headers)
  )

  const tableData = useMemo(() => {
    if (statusFilter === 'UNREAD') return data.filter((r) => r.STATUS === 'N')
    if (statusFilter === 'READ') return data.filter((r) => r.STATUS === 'Y')
    return data
  }, [data, statusFilter])

  const table = useReactTable({
    data: tableData,
    columns,
    columnResizeMode: COLUMN_RESIZE_MODE,
    columnResizeDirection: COLUMN_RESIZE_DIRECTION,
    state: { rowSelection, columnFilters, sorting, columnOrder, columnVisibility },
    defaultColumn: { size: 50 },
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues()
  })
  const { pageIndex } = table.getState().pagination
  const selectedCount = table.getSelectedRowModel().rows.length

  const selectedRows = table.getSelectedRowModel().flatRows
  const allSelectedUnread = selectedRows.length > 0 && selectedRows.every((r) => r.original.STATUS === 'N')
  const allSelectedRead = selectedRows.length > 0 && selectedRows.every((r) => r.original.STATUS === 'Y')
  const hasUnreadSelected = selectedRows.some((r) => r.original.STATUS === 'N')
  const hasReadSelected = selectedRows.some((r) => r.original.STATUS === 'Y')
  const isMixedSelection = hasUnreadSelected && hasReadSelected

  const fetchMessagesData = (silent = false) => {
    const vendorURL = `${API}/api/messages/po-messages?vendorId=${user?.supplierId}`
    const buyerURL = `${API}/api/messages/po-messages?buyer=${displayName?.toUpperCase()}`
    if (!silent) setLoading(true)
    axiosInstance
      .get(!isVendor ? buyerURL : vendorURL)
      .then((response) => {
        const result = response?.data?.items
        setData(Array.isArray(result) ? result : [])
        getUnseenMessage()
        setCounts({
          total: response?.data?.totalPoSku ?? 0,
          unseen: response?.data?.unseenPoSku ?? 0,
          seen: response?.data?.seenPoSku ?? 0
        })
      })
      .catch((error) => console.error(error))
      .finally(() => {
        if (!silent) setLoading(false)
      })
  }

  const markasUnread = () => {
    const rows = table.getSelectedRowModel().flatRows
    if (!rows.length) return
    const payload = {
      role,
      poSkuList: rows.map((row) => ({
        poNumber: row.original.PO_NUMBER,
        poSku: row.original.SKU,
      }))
    }

    setMarking(true)
    axiosInstance
      .put(`${API}/api/messages/markAsUnread`, payload)
      .then((res) => {
        setRowSelection({})
        fetchMessagesData(true)
        setMessageForBanner(
          <>
          <span style={{  fontWeight: 'bold' }}>{ res?.data?.message}</span>
        </>
         )
      })


      .catch((error) =>{
        console.error(error)
        const message = error?.response?.data?.message
        setMessageForBanner(
          <>
          <span style={{ color:'red', fontWeight: 'bold' }}>{message}</span>
        </>
         )
      }) 
      .finally(() => setMarking(false))
  }

  const markasRead = (po, sku) => {
    const rows = table.getSelectedRowModel().flatRows
    if (!rows.length && !(po && sku)) return

    const selected = rows.map((row) => ({
      poNumber: row.original.PO_NUMBER,
      poSku: row.original.SKU
    }))

    const payload = {
      role,
      poSkuList: (selected.length && selected) || [{ poNumber: po, poSku: sku }]
    }

    setMarking(true)
    axiosInstance
      .put(`${API}/api/messages/markAsRead`, payload)
      .then((res) => {
        if (!(po && sku)) {
          setRowSelection({})
          fetchMessagesData(true)
          setMessageForBanner(
            <>
            <span style={{  fontWeight: 'bold' }}>{ res?.data?.message}</span>
          </>
           )
        }
       
      })
      .catch((error) =>{
        console.error(error)
        const message = error?.response?.data?.message
        setMessageForBanner(
          <>
          <span style={{ color:'red', fontWeight: 'bold' }}>{ message}</span>
        </>
         )
      })
      .finally(() => setMarking(false))
  }

  useEffect(() => {
    fetchMessagesData()
  }, [])


  const loadColumnWidths = (cols) => {
    const storedWidths = JSON.parse(localStorage.getItem('column-widths')) || {}
    cols.forEach((column) => {
      if (storedWidths[column.id]) column.columnDef.size = Number(storedWidths[column.id])
    })
  }

  useEffect(() => {
    loadColumnWidths(table.getAllColumns())
  }, [table])

  useEffect(() => {
    const clearWidths = () => localStorage.removeItem('column-widths')
    window.addEventListener('beforeunload', clearWidths)
    return () => window.removeEventListener('beforeunload', clearWidths)
  }, [])

  const openMessagePanel = (po, sku) => {
    setSelectedPO(po)
    setSelectedSKU(sku)
    setIsMessageBoxOpen(true)
  }

  const closeMessagePanel = () => {
    setSelectedPO(null)
    setSelectedSKU(null)
    setIsMessageBoxOpen(false)
    fetchMessagesData(true)
  }

  const selectedRow = useMemo(
    () =>
      (Array.isArray(data || selectedRowValues) ? data || selectedRowValues : []).find(
        (r) => r.PO_NUMBER === selectedPO && r.SKU === selectedSKU
      ),
    [data, selectedPO, selectedSKU, selectedRowValues]
  )

  const handleColumnResizeEnd = (header) => {
    const storedWidths = JSON.parse(localStorage.getItem('column-widths')) || {}
    storedWidths[header.id] = header.getSize()
    localStorage.setItem('column-widths', JSON.stringify(storedWidths))
  }

  const onDragStart = (e) => {
    columnBeingDragged.current = Number(e.currentTarget.dataset.columnIndex)
  }
  const onDrop = (e) => {
    e.preventDefault()
    const from = columnBeingDragged.current
    const to = Number(e.currentTarget.dataset.columnIndex)
    if (from !== to && from >= 0 && to >= 0) {
      const currentCols = table.getVisibleLeafColumns().map((c) => c.id)
      const [moved] = currentCols.splice(from, 1)
      currentCols.splice(to, 0, moved)
      setColumnOrder(currentCols)
      addFilterData({ hftUserId: user?.supplierId, tableName: TABLE_NAME, filteredCol: currentCols })
    }
  }

  const handleMessageIconClick = (po, sku, rowData) => {
    setSelectedRowValues(rowData)
    openMessagePanel(po, sku)
  }

  const handleCloseMessageBox = () => {
    closeMessagePanel()
  }

  const cleared = () => {
    setColumnFilters([])
    setClearState((prev) => !prev)
    setFilterList({})
    addFilterData({ hftUserId: user?.supplierId, tableName: TABLE_NAME, clFilter: [] })
  }

  const saveToggleData = (e) => {
    const checked = e?.target?.checked
    const toggleList = {}
    table.getAllLeafColumns().forEach((column) => {
      column.toggleVisibility(checked)
      toggleList[column.id] = checked
    })
    addFilterData({
      hftUserId: user?.supplierId,
      tableName: TABLE_NAME,
      toggle_column: { ...table.getState().columnVisibility, ...toggleList }
    })
  }

  const saveToggleForEach = (e, column) => {
    column.toggleVisibility(e.target.checked)
    addFilterData({
      hftUserId: user?.supplierId,
      tableName: TABLE_NAME,
      toggle_column: { ...table.getState().columnVisibility, [column.id]: e.target.checked }
    })
  }

  const generateCSVData = () => table.getFilteredRowModel().rows.map((row) => row.original)


  return (
    <div className='w-full flex flex-col gap-4 p-3 max-h-screen'>

      <div className='w-full justify-between flex items-center'>
        <div className=''>
          <p className='text-xl'>{MessageView}</p>
        </div>
      </div>
      <div className='w-full text-xs flex justify-start items-center gap-1 font-medium text-gray-600'>
        <p className='flex gap-1 items-center justify-center'>
          <span
            className='hover:underline cursor-pointer'
            onClick={() => navigate('/vendor')}
          >
            {Home}
          </span>
          <span>{'>'}</span>
        </p>
        <p className='flex gap-1 items-center justify-center'>
          <span>{MessageView}</span>
        </p>
      </div>
      <div className='flex items-center gap-2 text-xs'>
        {[
          { key: 'ALL', label: ALL, count: counts.total },
          { key: 'UNREAD', label: UNREAD, count: counts.unseen },
          { key: 'READ', label: READ, count: counts.seen }
        ].map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            className={`flex items-center gap-2 px-3 h-[30px] rounded-md border font-medium ${statusFilter === key
              ? 'bg-[#415da1] text-white border-[#415da1]'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
          >
            {label}
            <span
              className={`min-w-[22px] px-1.5 rounded-full text-center text-[11px] font-semibold ${key === 'UNREAD'
                ? 'bg-red-500 text-white'
                : statusFilter === key
                  ? 'bg-white text-[#415da1]'
                  : 'bg-gray-200 text-gray-700'
                }`}
            >
              {count}
            </span>
          </button>
        ))}
      </div>

      <div className='w-full justify-between flex items-center text-xs'>
        <div className='relative flex justify-start items-center gap-2'>
          <div className='relative flex justify-start items-center'>
            <button
              className='font-medium text-white outline-none bg-[#415da1] w-[90px] rounded-md h-[30px]'
              onClick={() => setShowSelectColumn(!showSelectColumn)}
            >
              {Columns}
            </button>
            {showSelectColumn && (
              <div className='border border-black shadow rounded absolute top-10 h-[250px] w-[230px] overflow-y-auto bg-[#fff] z-20 flex flex-col gap-2'>
                <div className='p-1 px-2 border-b border-black flex gap-2 items-center py-2'>
                  <input
                    type='checkbox'
                    id='column_toggle_all'
                    checked={table.getIsAllColumnsVisible()}
                    onChange={saveToggleData}
                  />
                  <label htmlFor='column_toggle_all'>{ToggleAll}</label>
                </div>
                {table.getAllLeafColumns().map((column) => (
                  <div key={column.id} className='px-2 py-1 flex gap-2 items-center'>
                    <input
                      type='checkbox'
                      id={column.id}
                      checked={column.getIsVisible()}
                      onChange={(e) => saveToggleForEach(e, column)}
                    />
                    <label htmlFor={column.id}>{column.columnDef.header}</label>
                  </div>
                ))}
              </div>
            )}
          </div>



          <button
            className='font-medium text-white outline-none rounded-md h-[30px] px-3 min-w-max bg-[#415da1] disabled:bg-gray-400 disabled:cursor-not-allowed'
            disabled={selectedCount === 0 || marking ||  !allSelectedRead || isMixedSelection}
            onClick={() => markasUnread()}
          >
            {MarkAsUnread}
          </button>

          <button
            // key={label}
            className='font-medium text-white outline-none rounded-md h-[30px] px-3 min-w-max bg-[#415da1] disabled:bg-gray-400 disabled:cursor-not-allowed'
            disabled={selectedCount === 0 || marking || !allSelectedUnread || isMixedSelection}
            onClick={() => markasRead()}
          >
            {MarkAsRead}
          </button>
        </div>
        
        <div className='flex items-center gap-1'>
          {columnFilters?.length > 0 && (
            <button
              className='font-medium text-white outline-none bg-[#415da1] w-[90px] rounded-md h-[30px]'
              onClick={cleared}
            >
              {ClearFilters}
            </button>
          )}
          {data?.length !== 0 && downloadCSV('PO_list', generateCSVData())}
          <button
            className='border rounded p-1 font-medium text-sm'
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {'<<'}
          </button>
          <button
            className='border rounded p-1 font-medium text-sm'
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {'<'}
          </button>
          <button
            className='border rounded p-1 font-medium text-sm'
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {'>'}
          </button>
          <button
            className='border rounded p-1 font-medium text-sm'
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {'>>'}
          </button>
          <span className='font-medium min-w-max flex items-center gap-1'>
            <div>{Page}</div>
            <strong>
              {pageIndex + 1} of {table.getPageCount()}
            </strong>
          </span>
          <span className='min-w-max font-medium flex items-center gap-1'>
            | {Gotopage}
            <input
              type='number'
              id='pagination_input'
              value={pageIndex + 1}
              onChange={(e) => table.setPageIndex(e.target.value ? Number(e.target.value) - 1 : 0)}
              className='border p-1 rounded w-16'
            />
          </span>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
          >
            {CalculateTableArray(table.getFilteredRowModel().rows.length).map((size) => (
              <option key={size} value={size}>
                {Show} {size}
              </option>
            ))}
          </select>
        </div>
        
      </div>

      {isMixedSelection && (
        <p className='text-[11px] font-medium text-red-600'>
          Please select either read or unread messages to enable Mark as Read / Mark as Unread.
        </p>
      )}
      {!loading ? (
        <div className='w-full flex' style={{ minHeight: '70vh' }}>

          <div className='flex-1 min-w-0 overflow-x-auto'>
            <table className='text-xs border border-gray-300'>
              <thead className='top-0 z-10'>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className='divide-x-[1px] bg-[#e0ecfc] divide-gray-300 border-[#ccd9e4] border'
                  >
                    <th className='font-medium p-2 frozen-column1' style={{ minWidth: 138 }}>
                      <IndeterminateCheckbox
                        checked={table.getIsAllPageRowsSelected()}
                        indeterminate={table.getIsSomePageRowsSelected()}
                        onChange={table.getToggleAllPageRowsSelectedHandler()}
                      />
                    </th>
                    {headerGroup.headers.map((header) => {
                      const isPinned = header.id === 'PO_SKU'
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          style={{ width: header.getSize() }}
                          className={`font-medium p-2 top-0 ${isPinned ? 'frozen-column1' : 'unFrozen'}`}
                          draggable={!isPinned && !table.getState().columnSizingInfo.isResizingColumn}
                          data-column-index={header.index ?? -1}
                          onDragStart={isPinned ? undefined : onDragStart}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={isPinned ? undefined : onDrop}
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <div className='flex justify-center text-center h-9'>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                &nbsp;&nbsp;
                                <SortIcon direction={header.column.getIsSorted()} />
                              </div>
                            </div>
                          )}

                          {header.column.getCanFilter() && (
                            <div>
                              <Filter
                                column={header.column}
                                table={table}
                                columnFilters={columnFilters}
                                values={values}
                                setValues={setValues}
                                header={header}
                                ClearState={clearState}
                                tableId={TABLE_NAME}
                                filterValue={filterList}
                                hftid={user?.supplierId}
                                poshan='poshan'
                              />
                            </div>
                          )}
                          <div
                            onDoubleClick={() => header.column.resetSize()}
                            onMouseDown={(e) => {
                              header.getResizeHandler()(e)
                              document.addEventListener('mouseup', () => handleColumnResizeEnd(header), { once: true })
                            }}
                            onTouchStart={(e) => {
                              header.getResizeHandler()(e)
                              document.addEventListener('touchend', () => handleColumnResizeEnd(header), { once: true })
                            }}
                            className={`resizer ${table.options.columnResizeDirection} ${header.column.getIsResizing() ? 'isResizing' : ''}`}
                          />
                        </th>
                      )
                    })}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.map((row, index) => {
                  const { PO_NUMBER, SKU } = row.original
                  const unread = isUnread(row.original)
                  const isActive = isMessageBoxOpen && PO_NUMBER === selectedPO && SKU === selectedSKU

                  const rowBg = row.getIsSelected()
                    ? '#bfdbfe'
                    : isActive
                      ? '#dbeafe'
                      : unread > 0
                        ? '#ffe4e6'
                        : index % 2 !== 0
                          ? '#ecedf2'
                          : '#ffffff'
                  return (
                    <tr
                      key={row.id}
                      ref={forceBg(rowBg)}
                      onClick={() => openMessagePanel(PO_NUMBER, SKU)}
                      style={isActive ? { outlineOffset: '-3px' } : undefined}
                      className={`${rowBg} cursor-pointer text-center divide-x-[1px] divide-gray-300 border border-gray-300 ${row.getIsSelected() ? 'selected' : ''}`}
                    >
                      <td
                        ref={forceBg(rowBg)}
                        className='px-1.5 py-1.5 frozen-column1'
                        onClick={(e) => e.stopPropagation()} 
                      >
                        <div
                          className='w-full flex justify-start items-center gap-2 min-w-min'
                          style={{ width: '125px' }}
                        >
                          <IndeterminateCheckbox
                            checked={row.getIsSelected()}
                            indeterminate={row.getIsSomeSelected()}
                            onChange={row.getToggleSelectedHandler()}
                          />

                            <svg
                              className='w-4 h-4 text-gray-900 cursor-pointer'
                              aria-hidden='true'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 20 20'
                              onClick={() => handleCRHistoryClick(PO_NUMBER, SKU)}
                            >
                              <path
                                stroke='currentColor'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                d='M18 5h1v12a2 2 0 0 1-2 2m0 0a2 2 0 0 1-2-2V2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v15a2 2 0 0 0 2 2h14ZM10 4h2m-2 3h2m-8 3h8m-8 3h8m-8 3h8M4 4h3v3H4V4Z'
                              />
                            </svg>
                       
                          <span
                            className={`cursor-pointer ${unread > 0 ? 'text-red-500' : 'text-gray-500'}`}
                            onClick={() =>
                              handleMessageIconClick(PO_NUMBER, SKU, row?.original)
                            }
                            title={unread ? 'Unread messages' : 'No unread messages'}
                          >
                            {unread ? <MessageIconUnread /> : <MessageIconRead />}
                          </span>
                        </div>
                      </td>

                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          ref={forceBg(rowBg)}
                          style={{ width: cell.column.getSize() }}
                          className={`px-1.5 py-1.5 ${cell.column.id === 'PO_SKU' ? 'frozen-column1' : ''}`}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {isMessageBoxOpen && (
            <aside
              className='fixed right-0 z-30 w-[480px] flex flex-col overflow-hidden border-l-[3px] border-b-[3px] bg-white '
              style={{ top: 51, height: `calc(100vh - ${51 + 24}px)` }}
            >
              <div className='shrink-0 flex items-center justify-between p-3 bg-[#e0ecfc]'>
                <p className='font-medium text-sm'>
                  PO {selectedPO || selectedRowValues?.PO_NUMBER} - SKU {selectedSKU || selectedRowValues?.SKU}
                </p>
                <button
                  className='border border-gray-300 bg-white rounded-md px-2 py-1 text-xs'
                  onClick={closeMessagePanel}
                >
                  ✕
                </button>
              </div>

              <div className='shrink-0 grid grid-cols-2 gap-3 p-3 border-b border-gray-300'>
                <PoField label='Vendor #' value={selectedRow?.['VENDOR#'] || selectedRowValues?.['VENDOR#']} />
                <PoField label='Vendor Name' value={selectedRow?.VENDOR_NAME || selectedRowValues?.VENDOR_NAME} />
                <PoField label='UPC' value={selectedRow?.UPC || selectedRowValues?.UPC} />
                <PoField label='Description' value={selectedRow?.DESCRIPTION || selectedRowValues?.DESCRIPTION} />
              </div>


              <MessageBox
                po={selectedPO}
                sku={selectedSKU}
                vendorId={selectedRow?.['VENDOR#'] || selectedRowValues?.['VENDOR#']}
                onClose={handleCloseMessageBox}
                // fetchMessagesData={() => fetchMessagesData(true)}
                messageUnreadCount={isUnread(selectedRow) ? 1 : 0}
                isMessageView={true}
                markasRead={markasRead}
              />

            </aside>
          )}
        </div>
      ) : (
        <div className='w-full min-h-[450px] flex flex-col gap-3 justify-center items-center text-xs font-medium'>
          <div className='w-6 h-6 border-4 border-gray-200 border-t-gray-600 rounded-full animate-spin' />
          <p>{Loading1}</p>
        </div>
      )}
       {messageForBanner &&
        <PopupBanner
          isOpen={messageForBanner}
          message={messageForBanner}
          onCancel={() => setMessageForBanner('')}
        />
      }
    </div>
  )
}

export default MessageView